import type {
    OpenCallbackProps,
    OpenSubscriptionModalCallback,
} from '@proton/components/containers/payments/subscription/SubscriptionModalProvider';
import { SUBSCRIPTION_STEPS } from '@proton/components/containers/payments/subscription/constants';
import { type COUPON_CODES, CYCLE } from '@proton/payments/core/constants';
import type { PlanIDs } from '@proton/payments/core/interface';
import type { Plan } from '@proton/payments/core/plan/interface';
import type { MaybeFreeSubscription } from '@proton/payments/core/subscription/helpers';
import { APPS_WITH_IN_APP_PAYMENTS, type APP_NAMES } from '@proton/shared/lib/constants';
import { addUpsellPath, getUpgradePath } from '@proton/shared/lib/helpers/upsell';
import { formatURLForAjaxRequest } from '@proton/shared/lib/helpers/url';
import type { UserModel } from '@proton/shared/lib/interfaces';
import noop from '@proton/utils/noop';

export interface GetUpsellConfigProps {
    upsellRef?: string;
    /**
     * @default SUBSCRIPTION_STEPS.CHECKOUT
     */
    step?: SUBSCRIPTION_STEPS;
    coupon?: COUPON_CODES;
    /**
     * @default CYCLE.YEARLY
     */
    cycle?: CYCLE;
    /**
     * @default CYCLE.YEARLY
     */
    maximumCycle?: CYCLE;
    minimumCycle?: CYCLE;
    plan?: Plan['Name'];
    onSubscribed?: () => void;
    /**
     * Can be used to prevent the modal from being opened in the drawer
     * @default false
     */
    preventInApp?: boolean;
}

export const getUpsellConfig = ({
    appName,
    configOverride,
    coupon,
    cycle = CYCLE.YEARLY,
    maximumCycle,
    minimumCycle,
    onSubscribed,
    openSubscriptionModal,
    planIDs,
    preventInApp = false,
    step = SUBSCRIPTION_STEPS.CHECKOUT,
    subscription,
    upsellRef,
    user,
}: Omit<GetUpsellConfigProps, 'plan'> & {
    appName: APP_NAMES;
    planIDs?: PlanIDs;
    openSubscriptionModal: OpenSubscriptionModalCallback | undefined;
    subscription: MaybeFreeSubscription;
    user: UserModel;
    /** Override final config before opening subscription modal */
    configOverride?: (config: OpenCallbackProps) => void;
}): { upgradePath: string; onUpgrade?: () => Promise<void> } => {
    const hasInAppPayments = APPS_WITH_IN_APP_PAYMENTS.has(appName);

    if (openSubscriptionModal && hasInAppPayments && upsellRef && !preventInApp) {
        const subscriptionCallBackProps: OpenCallbackProps = {
            coupon,
            cycle,
            disablePlanSelection: step === SUBSCRIPTION_STEPS.CHECKOUT,
            maximumCycle,
            minimumCycle,
            mode: 'upsell-modal', // hide the Free plan
            step,
            upsellRef,
        };

        if (planIDs) {
            subscriptionCallBackProps.planIDs = planIDs;
        }

        configOverride?.(subscriptionCallBackProps);

        // The subscription modal will open in inbox app
        return {
            upgradePath: '',
            onUpgrade() {
                // Generate a mocked request to track upsell activity
                const urlParameters = { ref: upsellRef, load: 'modalOpen' };
                const url = formatURLForAjaxRequest(window.location.href, urlParameters);
                fetch(url).catch(noop);

                // Open the subscription modal
                return openSubscriptionModal({ ...subscriptionCallBackProps, onSubscribed });
            },
        };
    }

    // The user will be redirected to account app
    return { upgradePath: addUpsellPath(getUpgradePath({ user, subscription, app: appName }), upsellRef) };
};
